import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { casesApi, type CaseRow, type CaseStatus, type CaseUpdateInput } from '../../lib/api/cases';
import { deviceModelsApi, type DeviceModel } from '../../lib/api/devices';
import { statusLabelKey } from './caseStatusLabel';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './CaseUpdateDialog.module.css';
import { deviceModelLabel } from '../devices/deviceFormHelpers';

/**
 * The case edit form — `PATCH /cases/{case}`, gated on `case.update`
 * (`CaseDetail.tsx`'s own docblock: this dialog is never even mounted with
 * `open` for a session without it, nor for a case that is already closed).
 *
 * `status` EXCLUDES `'closed'` FROM ITS OWN OPTIONS — `UpdateCaseRequest`'s
 * own rule (`cases.ts`'s own docblock: "sending it here is a plain 422;
 * only `POST /cases/{uuid}/close` may ever close a case"), so this select
 * never offers a value the API would refuse.
 *
 * `reader_serial`/`reader_type`/`sensor_serial`/`sensor_type` (M-08 Task 2,
 * M-06's K-9's missing columns) ARE SHOWN UNCONDITIONALLY HERE, UNLIKE
 * `CaseFormDialog`'s per-line gating — an edit can add a serial or
 * generation to a line that was left blank at create time, so there is no
 * "line not requested" state to hide the field for.
 */

export interface CaseUpdateDialogProps {
  open: boolean;
  record: CaseRow;
  onClose: () => void;
  onSaved: (updated: CaseRow) => void;
}

const STATUS_OPTIONS: readonly Exclude<CaseStatus, 'closed'>[] = [
  'open',
  'in_progress',
  'waiting',
  'escalated',
  'cancelled',
];

interface FormState {
  status: string;
  perceptionCode: string;
  assessmentCode: string;
  agentNotes: string;
  readerSerial: string;
  readerType: string;
  sensorSerial: string;
  sensorType: string;
}

function stateFor(record: CaseRow): FormState {
  return {
    status: record.status === 'closed' ? 'open' : record.status,
    perceptionCode: record.perception_code ?? '',
    assessmentCode: record.assessment_code ?? '',
    agentNotes: record.agent_notes ?? '',
    readerSerial: record.reader_serial ?? '',
    readerType: record.reader_type ?? '',
    sensorSerial: record.sensor_serial ?? '',
    sensorType: record.sensor_type ?? '',
  };
}

const FORBIDDEN_CODE = 'forbidden';

export function CaseUpdateDialog({ open, record, onClose, onSaved }: CaseUpdateDialogProps) {
  const { t, i18n } = useTranslation();

  const [state, setState] = useState<FormState>(() => stateFor(record));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deviceModels, setDeviceModels] = useState<readonly DeviceModel[]>([]);

  const inFlight = useRef(false);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    setState(stateFor(record));
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, record]);

  // Same catalogue fetch `CaseFormDialog` uses for its own generation
  // `Select`s — M-08 Task 2.
  useEffect(() => {
    if (!open) return;
    void deviceModelsApi.list().then(setDeviceModels);
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('cases.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('cases.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('cases.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const input: CaseUpdateInput = {
        status: state.status as Exclude<CaseRow['status'], 'closed'>,
        perception_code: state.perceptionCode.trim() === '' ? null : state.perceptionCode.trim(),
        assessment_code: state.assessmentCode.trim() === '' ? null : state.assessmentCode.trim(),
        agent_notes: state.agentNotes.trim() === '' ? null : state.agentNotes.trim(),
        // M-08 Task 2 — unconditional, unlike the create form's per-line
        // gating: an update can add a serial to a line that was left
        // blank at create time (this file's own docblock).
        reader_serial: state.readerSerial.trim() === '' ? null : state.readerSerial.trim(),
        reader_type: state.readerType.trim() === '' ? null : state.readerType.trim(),
        sensor_serial: state.sensorSerial.trim() === '' ? null : state.sensorSerial.trim(),
        sensor_type: state.sensorType.trim() === '' ? null : state.sensorType.trim(),
      };
      const saved = await casesApi.update(record.uuid, input);
      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const statusOptions: readonly SelectOption[] = STATUS_OPTIONS.map((value) => ({
    value,
    label: t(statusLabelKey(value)),
  }));

  const readerModelOptions: readonly SelectOption[] = deviceModels
    .filter((m) => m.kind === 'reader')
    .map((m) => ({ value: m.code, label: deviceModelLabel(m, i18n.language) }));
  const sensorModelOptions: readonly SelectOption[] = deviceModels
    .filter((m) => m.kind === 'sensor')
    .map((m) => ({ value: m.code, label: deviceModelLabel(m, i18n.language) }));

  return (
    <Dialog
      open={open}
      kind="form"
      size="md"
      onClose={onClose}
      title={t('cases.editTitle', { caseNumber: record.case_number })}
    >
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true" data-testid="caseUpdateFailure">
          {generalFailure}
        </div>

        <Select
          label={t('table.status')}
          width="compact"
          required
          options={statusOptions}
          value={state.status}
          error={fieldErrors['status']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, status: value }));
          }}
        />

        {/*
          Three pairs of `width="compact"` fields, each pair genuinely short
          together — a code, or a serial next to the type it belongs to — laid
          out one row each instead of stacking six half-empty rows inside the
          wider `md` dialog. No declared `flex-direction` to mirror: `.row`'s
          default `row` already reverses under `dir="rtl"`. Order within the
          form is unchanged; only the wrapping is new.
        */}
        <div className={styles['row']}>
          <Input
            label={t('cases.fieldPerceptionCode')}
            width="compact"
            dir="ltr"
            value={state.perceptionCode}
            error={fieldErrors['perception_code']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, perceptionCode: value }));
            }}
          />

          <Input
            label={t('cases.fieldAssessmentCode')}
            width="compact"
            dir="ltr"
            value={state.assessmentCode}
            error={fieldErrors['assessment_code']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, assessmentCode: value }));
            }}
          />
        </div>

        <div className={styles['row']}>
          <Input
            label={t('cases.readerSerial')}
            width="compact"
            dir="ltr"
            value={state.readerSerial}
            error={fieldErrors['reader_serial']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, readerSerial: value }));
            }}
          />

          <Select
            label={t('cases.readerType')}
            width="compact"
            placeholder={t('cases.chooseType')}
            options={readerModelOptions}
            value={state.readerType}
            error={fieldErrors['reader_type']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, readerType: value }));
            }}
          />
        </div>

        <div className={styles['row']}>
          <Input
            label={t('cases.sensorSerial')}
            width="compact"
            dir="ltr"
            value={state.sensorSerial}
            error={fieldErrors['sensor_serial']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, sensorSerial: value }));
            }}
          />

          <Select
            label={t('cases.sensorType')}
            width="compact"
            placeholder={t('cases.chooseType')}
            options={sensorModelOptions}
            value={state.sensorType}
            error={fieldErrors['sensor_type']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, sensorType: value }));
            }}
          />
        </div>

        <Textarea
          ref={notesRef}
          label={t('cases.fieldAgentNotes')}
          dir="auto"
          value={state.agentNotes}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, agentNotes: value }));
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting}
            icon={<FloppyDiskIcon size={16} />}
          >
            {t('actions.saveCase')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('cases.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
